import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import { initializeApp } from 'firebase/app'
import { 
  getAuth, 
  signInWithEmailAndPassword, 
  createUserWithEmailAndPassword,
  signInWithPopup,
  GoogleAuthProvider,
  signOut,
  onAuthStateChanged,
  User as FirebaseUser
} from 'firebase/auth'
import axios from 'axios'
import toast from 'react-hot-toast'
import { User, SupportedLanguage } from '@tvia-ai/shared'

// Firebase configuration
const firebaseConfig = {
  apiKey: import.meta.env.VITE_FIREBASE_API_KEY,
  authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN,
  projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID,
  storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID,
  appId: import.meta.env.VITE_FIREBASE_APP_ID,
}

// Initialize Firebase
const app = initializeApp(firebaseConfig)
const auth = getAuth(app)
const googleProvider = new GoogleAuthProvider()

// Configure axios defaults
// In development, use Vite proxy (no baseURL needed)
// In production, use the API URL from environment
if (import.meta.env.PROD) {
  axios.defaults.baseURL = import.meta.env.VITE_API_URL || 'http://localhost:5000'
}
// In development, Vite proxy handles /api routes automatically

// Add axios interceptor for authentication error handling and token refresh
axios.interceptors.response.use(
  response => response,
  async error => {
    const originalRequest = error.config;
    // If we get an authentication error from the backend
    if (
      error.response &&
      error.response.data &&
      (error.response.data.error === 'Authentication failed' || error.response.data.error === 'No token provided') &&
      !originalRequest._retry
    ) {
      originalRequest._retry = true;
      try {
        // Try to refresh the Firebase token
        const currentUser = auth.currentUser;
        if (currentUser) {
          const newToken = await currentUser.getIdToken(true); // force refresh
          axios.defaults.headers.common['Authorization'] = `Bearer ${newToken}`;
          originalRequest.headers['Authorization'] = `Bearer ${newToken}`;
          // Update token in zustand store
          useAuthStore.setState({ token: newToken });
          return axios(originalRequest);
        }
      } catch (refreshError) {
        // If refresh fails, log out
        useAuthStore.getState().logout();
      }
    }
    return Promise.reject(error);
  }
);

// Add axios request interceptor to check token validity before sending requests
axios.interceptors.request.use(
  async config => {
    const currentUser = auth.currentUser;
    if (currentUser) {
      // Check if token is expired or about to expire
      const tokenResult = await currentUser.getIdTokenResult();
      const expiresAt = new Date(tokenResult.expirationTime).getTime();
      const now = Date.now();
      // If token expires in less than 1 minute, refresh it
      if (expiresAt - now < 60 * 1000) {
        const newToken = await currentUser.getIdToken(true);
        axios.defaults.headers.common['Authorization'] = `Bearer ${newToken}`;
        config.headers['Authorization'] = `Bearer ${newToken}`;
        useAuthStore.setState({ token: newToken });
      }
    }
    return config;
  },
  error => Promise.reject(error)
);

interface AuthState {
  user: User | null
  token: string | null
  isLoading: boolean
  isAuthenticated: boolean
  pendingTermsApproval: boolean
  
  // Actions
  login: (email: string, password: string) => Promise<void>
  register: (userData: {
    email: string
    password: string
    firstName: string
    lastName: string
    preferredLanguage: SupportedLanguage
    country?: string
    acceptTerms?: boolean
  }) => Promise<void>
  loginWithGoogle: () => Promise<void>
  registerWithGoogle: () => Promise<void>
  acceptTerms: (termsVersion?: string) => Promise<void>
  logout: () => Promise<void>
  checkAuth: () => Promise<void>
  updateProfile: (userData: Partial<User>) => Promise<void>
  changeLanguage: (language: SupportedLanguage) => Promise<void>
  getAuthToken: () => string | null
  refreshUser: () => Promise<void>
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      user: null,
      token: null,
      isLoading: true,
      isAuthenticated: false,
      pendingTermsApproval: false,

      login: async (email: string, password: string) => {
        try {
          set({ isLoading: true })
          
          // Sign in with Firebase
          const credential = await signInWithEmailAndPassword(auth, email, password)
          const token = await credential.user.getIdToken()
          
          // Get user data from backend
          const response = await axios.get('/api/auth/me', {
            headers: { Authorization: `Bearer ${token}` }
          })
          
          const user = response.data.data.user
          
          // Set axios default authorization header
          axios.defaults.headers.common['Authorization'] = `Bearer ${token}`
          
          set({
            user,
            token,
            isAuthenticated: true,
            isLoading: false,
            pendingTermsApproval: !user.termsAcceptedAt, // Set pending if terms not accepted
          })
          
          toast.success('Login successful!')
        } catch (error: any) {
          console.error('Login error:', error)
          toast.error(error.response?.data?.error || 'Login failed')
          set({ isLoading: false })
          throw error
        }
      },

      register: async (userData) => {
        try {
          set({ isLoading: true })
          
          // Create user with Firebase
          const credential = await createUserWithEmailAndPassword(
            auth, 
            userData.email, 
            userData.password
          )
          const token = await credential.user.getIdToken()
          
          // Register user in backend
          const response = await axios.post('/api/auth/register', {
            email: userData.email,
            firstName: userData.firstName,
            lastName: userData.lastName,
            preferredLanguage: userData.preferredLanguage,
            country: userData.country,
            acceptTerms: userData.acceptTerms,
          }, {
            headers: { Authorization: `Bearer ${token}` }
          })
          
          const user = response.data.data.user
          
          // Set axios default authorization header
          axios.defaults.headers.common['Authorization'] = `Bearer ${token}`
          
          set({
            user,
            token,
            isAuthenticated: true,
            isLoading: false,
            pendingTermsApproval: !user.termsAcceptedAt, // Set pending if terms not accepted
          })
          
          toast.success('Registration successful!')
        } catch (error: any) {
          console.error('Registration error:', error)
          toast.error(error.response?.data?.error || 'Registration failed')
          set({ isLoading: false })
          throw error
        }
      },

      loginWithGoogle: async () => {
        try {
          set({ isLoading: true })
          
          // Sign in with Google
          const result = await signInWithPopup(auth, googleProvider)
          const token = await result.user.getIdToken()
          
          // Check if user exists in backend, create if not
          let response
          try {
            response = await axios.get('/api/auth/me', {
              headers: { Authorization: `Bearer ${token}` }
            })
          } catch (error: any) {
            if (error.response?.status === 404) {
              // User doesn't exist, create them
              const names = result.user.displayName?.split(' ') || ['User', '']
              response = await axios.post('/api/auth/register', {
                email: result.user.email,
                firstName: names[0],
                lastName: names.slice(1).join(' ') || '',
                preferredLanguage: 'en',
              }, {
                headers: { Authorization: `Bearer ${token}` }
              })
            } else {
              throw error
            }
          }
          
          const user = response.data.data.user
          
          // Set axios default authorization header
          axios.defaults.headers.common['Authorization'] = `Bearer ${token}`
          
          set({
            user,
            token,
            isAuthenticated: true,
            isLoading: false,
            pendingTermsApproval: !user.termsAcceptedAt, // Set pending if terms not accepted
          })
          
          toast.success('Login successful!')
        } catch (error: any) {
          console.error('Google login error:', error)
          toast.error(error.response?.data?.error || 'Google login failed')
          set({ isLoading: false, pendingTermsApproval: false })
          throw error
        }
      },

      logout: async () => {
        try {
          await signOut(auth)
          
          // Clear axios authorization header
          delete axios.defaults.headers.common['Authorization']
          
          set({
            user: null,
            token: null,
            isAuthenticated: false,
            isLoading: false,
          })
          
          toast.success('Logout successful!')
        } catch (error: any) {
          console.error('Logout error:', error)
          toast.error('Logout failed')
        }
      },

      checkAuth: async () => {
        return new Promise((resolve) => {
          const unsubscribe = onAuthStateChanged(auth, async (firebaseUser: FirebaseUser | null) => {
            try {
              if (firebaseUser) {
                const token = await firebaseUser.getIdToken()
                
                // Verify token with backend
                const response = await axios.get('/api/auth/me', {
                  headers: { Authorization: `Bearer ${token}` }
                })
                
                const user = response.data.data.user
                
                // Set axios default authorization header
                axios.defaults.headers.common['Authorization'] = `Bearer ${token}`
                
                set({
                  user,
                  token,
                  isAuthenticated: true,
                  isLoading: false,
                  pendingTermsApproval: !user.termsAcceptedAt, // Set pending if terms not accepted
                })
              } else {
                // Clear axios authorization header
                delete axios.defaults.headers.common['Authorization']
                
                set({
                  user: null,
                  token: null,
                  isAuthenticated: false,
                  isLoading: false,
                  pendingTermsApproval: false,
                })
              }
            } catch (error) {
              console.error('Auth check error:', error)
              
              // Clear axios authorization header
              delete axios.defaults.headers.common['Authorization']
              
              set({
                user: null,
                token: null,
                isAuthenticated: false,
                isLoading: false,
                pendingTermsApproval: false,
              })
            }
            
            unsubscribe()
            resolve(undefined)
          })
        })
      },

      updateProfile: async (userData) => {
        try {
          const { token } = get()
          if (!token) throw new Error('Not authenticated')
          
          const response = await axios.put('/api/auth/profile', userData, {
            headers: { Authorization: `Bearer ${token}` }
          })
          
          const updatedUser = response.data.data.user
          
          set({ user: updatedUser })
          toast.success('Profile updated successfully!')
        } catch (error: any) {
          console.error('Update profile error:', error)
          toast.error(error.response?.data?.error || 'Failed to update profile')
          throw error
        }
      },

      changeLanguage: async (language: SupportedLanguage) => {
        try {
          const currentUser = get().user
          const token = get().token
          if (!currentUser || !token) {
            throw new Error('Not authenticated')
          }

          // Call backend to update preferred language
          const response = await axios.post(
            '/api/users/preferences/language',
            { preferredLanguage: language },
            { headers: { Authorization: `Bearer ${token}` } }
          )

          // Update user in store locally only after successful backend update
          set({
            user: {
              ...currentUser,
              preferredLanguage: response.data.data.preferredLanguage,
            },
          })

          toast.success('Language updated successfully!')
        } catch (error: any) {
          console.error('Change language error:', error)
          toast.error(
            error.response?.data?.error || 'Failed to change language'
          )
          throw error
        }
      },

      getAuthToken: () => {
        return get().token;
      },

      refreshUser: async () => {
        try {
          const { token } = get()
          if (!token) throw new Error('Not authenticated')
          
          const response = await axios.get('/api/auth/me', {
            headers: { Authorization: `Bearer ${token}` }
          })
          
          const user = response.data.data.user
          
          set({ user })
        } catch (error: any) {
          console.error('Refresh user error:', error)
          throw error
        }
      },

      // Alias for registerWithGoogle (same as loginWithGoogle)
      registerWithGoogle: async () => {
        return get().loginWithGoogle();
      },

      acceptTerms: async (termsVersion: string = '1.0') => {
        try {
          set({ isLoading: true })
          
          const token = get().token
          if (!token) {
            throw new Error('No authentication token available')
          }
          
          // Send terms acceptance to backend
          const response = await axios.post('/api/auth/accept-terms', {
            termsVersion
          }, {
            headers: { Authorization: `Bearer ${token}` }
          })
          
          const updatedUser = response.data.data.user
          
          set({
            user: updatedUser,
            isLoading: false,
            pendingTermsApproval: false,
          })
          
          toast.success('Terms accepted successfully!')
        } catch (error: any) {
          console.error('Accept terms error:', error)
          toast.error(error.response?.data?.error || 'Failed to accept terms')
          set({ isLoading: false })
          throw error
        }
      },
    }),
    {
      name: 'auth-storage',
      partialize: (state) => ({
        user: state.user,
        token: state.token,
        isAuthenticated: state.isAuthenticated,
      }),
    }
  )
) 